import { memo, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { StatisticsContext } from '../../../../../providers/StatisticsProvider';
import { useProductContext } from '../../../../../providers/ProductProvider';
import { useGetCustomersQuery } from '../../../../../slices/customersApiSlice';
import { useGetAssistantUsersQuery, useGetSaleUsersQuery } from '../../../../../slices/usersApiSlice';
import {
	buildStatisticsFilterTags,
	resolveStatisticsCreatedDateRangeLabel,
	resolveStatisticsCompletedDateRangeLabel,
} from '../../../filters/lib/statisticsFilterTags';
import {
	resolveTrendPeriodSummary,
	resolvePersistedChartKind,
	resolveOverdueChartConfigFromSnapshot,
	PERSISTED_CHART_KIND,
} from '../lib/statisticsPersistedCharts';
import {
	formatPinnedAtLabel,
	isFrozenChartSnapshot,
	isPageBoundChartSnapshot,
	resolveDashboardChartBindingLabel,
	resolveDashboardChartRefreshLabel,
} from '../lib/statisticsChartRefreshMode';
import { resolvePersistedChartViewFilters } from '../lib/dashboardChartLifecycle';
import { getOrderPrimaryPhaseLabel } from '../../pipeline/lib/orderPrimaryPhaseConfig';
import { useOptionalStatisticsStatsState } from '../../../providers/StatisticsPageProvider';
import { useStatisticsCapabilities } from '../../../common/lib/statisticsAccess';
import { resolveDashboardBarMetric } from '../lib/dashboardPresetLayout';
import { getOverdueChartDimensionLabel } from '../../overdue/lib/overdueChartConfig';
import {
	SCOPE_TAG_EXCLUDE_IDS,
	resolveSavedChartCompositionParts,
} from '../lib/dashboardChartExpandMeta';

const DashboardChartMeta = ({ snapshot, show = false, isManager = false }) => {
	const { statisticsFilters } = useContext(StatisticsContext) ?? {};
	const statsState = useOptionalStatisticsStatsState();
	const { dashboardVariant } = useStatisticsCapabilities();
	const { fetchedProducts = [] } = useProductContext();
	const { data: fetchedSaleUsers = [] } = useGetSaleUsersQuery({ scope: 'order' }, { skip: !show });
	const { data: fetchedAssistantUsers = [] } = useGetAssistantUsersQuery({ scope: 'order' }, { skip: !show });
	const { data: fetchedCustomers = [] } = useGetCustomersQuery(undefined, { skip: !show || !isManager });

	const chartKind = snapshot ? resolvePersistedChartKind(snapshot) : null;
	const isOverdueChart = chartKind === PERSISTED_CHART_KIND.OVERDUE;
	const isPageBound = snapshot ? isPageBoundChartSnapshot(snapshot) : false;

	const filters = useMemo(() => {
		if (!snapshot) return null;
		return resolvePersistedChartViewFilters(snapshot, isPageBound ? statisticsFilters : null);
	}, [isPageBound, statisticsFilters, snapshot]);

	const tagContext = useMemo(
		() => ({
			saleUsers: fetchedSaleUsers,
			assistantUsers: fetchedAssistantUsers,
			customerOptions: fetchedCustomers.map((customer) => ({
				value: customer._id,
				label: customer.name || '未知客户',
			})),
			productOptions: fetchedProducts.map((product) => ({
				value: String(product._id),
				label: `${product.productCode ?? ''} ${product.name ?? ''}`.trim() || '未知产品',
			})),
		}),
		[fetchedAssistantUsers, fetchedCustomers, fetchedProducts, fetchedSaleUsers],
	);

	const compositionRuntime = useMemo(() => {
		const activeMetric =
			chartKind === PERSISTED_CHART_KIND.PIPELINE_BAR
				? resolveDashboardBarMetric(dashboardVariant, statsState?.activeMetric ?? snapshot?.activeMetric)
				: statsState?.activeMetric ?? snapshot?.activeMetric;
		return {
			activeMetric,
			activeDimension: statsState?.activeDimension,
			overdueConfig: resolveOverdueChartConfigFromSnapshot(snapshot),
		};
	}, [chartKind, dashboardVariant, snapshot, statsState?.activeDimension, statsState?.activeMetric]);

	const compositionParts = useMemo(
		() => resolveSavedChartCompositionParts(snapshot, filters, compositionRuntime),
		[compositionRuntime, filters, snapshot],
	);

	const bindingLabel = useMemo(() => resolveDashboardChartBindingLabel(snapshot), [snapshot]);
	const refreshLabel = useMemo(() => resolveDashboardChartRefreshLabel(snapshot), [snapshot]);

	const phaseLabel = useMemo(() => {
		const selectedPhase = statsState?.selectedPhase ?? snapshot?.selectedPhase;
		if (!selectedPhase) return '';
		return getOrderPrimaryPhaseLabel(selectedPhase);
	}, [snapshot?.selectedPhase, statsState?.selectedPhase]);

	const orderRangeLabel = useMemo(() => {
		if (!filters) return '';
		return (
			resolveStatisticsCreatedDateRangeLabel(filters.startDate, filters.endDate, filters.trendAnchor) || '不限'
		);
	}, [filters]);

	const completedRangeLabel = useMemo(() => {
		if (!filters) return '';
		return (
			resolveStatisticsCompletedDateRangeLabel(
				filters.completedStartDate,
				filters.completedEndDate,
				filters.completedDateAnchor,
			) || '不限'
		);
	}, [filters]);

	const compareSummary = useMemo(() => {
		if (!filters || chartKind === PERSISTED_CHART_KIND.COMPOSITION) return '';
		return resolveTrendPeriodSummary(filters);
	}, [chartKind, filters]);

	const scopeTags = useMemo(() => {
		if (!filters) return [];
		return buildStatisticsFilterTags(filters, tagContext).filter((tag) => !SCOPE_TAG_EXCLUDE_IDS.has(tag.id));
	}, [filters, tagContext]);

	const fixedAtLabel = useMemo(() => {
		if (isFrozenChartSnapshot(snapshot) && snapshot?.pinnedAt) {
			return formatPinnedAtLabel(snapshot.pinnedAt);
		}
		if (!snapshot?.createdAt) return '';
		const parsed = moment(snapshot.createdAt);
		return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : '';
	}, [snapshot]);

	const caliberSummary = `${bindingLabel.short} · ${refreshLabel.short}`;

	if (!snapshot) return null;

	const hasScopeRow = Boolean(phaseLabel || orderRangeLabel || completedRangeLabel || compareSummary);
	const hasMeta =
		compositionParts.length > 0 ||
		caliberSummary ||
		hasScopeRow ||
		scopeTags.length > 0 ||
		fixedAtLabel ||
		isOverdueChart;

	if (!hasMeta) return null;

	if (isOverdueChart) {
		const overdueDimensionLabel = getOverdueChartDimensionLabel(compositionRuntime.overdueConfig?.dimension);
		return (
			<div className='statistics-persisted-chart-modal__meta' role='note' aria-label='报表口径与筛选摘要'>
				{compositionParts.length > 0 ? (
					<p className='statistics-persisted-chart-modal__meta-row'>
						<span className='statistics-persisted-chart-modal__meta-label'>组成</span>
						<span className='statistics-persisted-chart-modal__meta-value'>{compositionParts.join(' · ')}</span>
					</p>
				) : null}
				<p className='statistics-persisted-chart-modal__meta-row'>
					<span className='statistics-persisted-chart-modal__meta-label'>口径</span>
					<span className='statistics-persisted-chart-modal__meta-value' title={`${bindingLabel.title}；${refreshLabel.title}`}>
						{caliberSummary}
					</span>
				</p>
				{hasScopeRow ? (
					<p className='statistics-persisted-chart-modal__meta-row'>
						<span className='statistics-persisted-chart-modal__meta-label'>范围</span>
						<span className='statistics-persisted-chart-modal__meta-value'>
							{[
								phaseLabel ? `阶段 ${phaseLabel}` : null,
								orderRangeLabel ? `订单范围 ${orderRangeLabel}` : null,
								completedRangeLabel ? `完成日期 ${completedRangeLabel}` : null,
							]
								.filter(Boolean)
								.join(' · ')}
						</span>
					</p>
				) : null}
				{scopeTags.length > 0 ? (
					<div className='statistics-persisted-chart-modal__meta-tags'>
						<span className='statistics-persisted-chart-modal__meta-label'>筛选</span>
						<div className='statistics-persisted-chart-modal__meta-tag-list'>
							{scopeTags.map((tag) => (
								<span key={tag.id} className='order-filter-chip statistics-filter-console__chip'>
									<span className='order-filter-chip__key'>{tag.label}</span>
								</span>
							))}
						</div>
					</div>
				) : null}
				<p className='statistics-persisted-chart-modal__meta-row'>
					<span className='statistics-persisted-chart-modal__meta-label'>数据</span>
					<span className='statistics-persisted-chart-modal__meta-value'>
						全库未付应收账款，不受顶栏筛选影响
						{overdueDimensionLabel ? ` · 当前维度 ${overdueDimensionLabel}` : ''}
					</span>
				</p>
				{fixedAtLabel ? (
					<p className='statistics-persisted-chart-modal__meta-footnote'>
						{isFrozenChartSnapshot(snapshot) ? `定格于 ${fixedAtLabel}` : `固定于 ${fixedAtLabel}`}
					</p>
				) : null}
			</div>
		);
	}

	if (!filters) return null;

	return (
		<div className='statistics-persisted-chart-modal__meta' role='note' aria-label='报表口径与筛选摘要'>
			{compositionParts.length > 0 ? (
				<p className='statistics-persisted-chart-modal__meta-row'>
					<span className='statistics-persisted-chart-modal__meta-label'>组成</span>
					<span className='statistics-persisted-chart-modal__meta-value'>{compositionParts.join(' · ')}</span>
				</p>
			) : null}
			<p className='statistics-persisted-chart-modal__meta-row'>
				<span className='statistics-persisted-chart-modal__meta-label'>口径</span>
				<span className='statistics-persisted-chart-modal__meta-value' title={`${bindingLabel.title}；${refreshLabel.title}`}>
					{caliberSummary}
				</span>
			</p>
			{hasScopeRow ? (
				<p className='statistics-persisted-chart-modal__meta-row'>
					<span className='statistics-persisted-chart-modal__meta-label'>范围</span>
					<span className='statistics-persisted-chart-modal__meta-value'>
						{[
							phaseLabel ? `阶段 ${phaseLabel}` : null,
							orderRangeLabel ? `订单范围 ${orderRangeLabel}` : null,
							completedRangeLabel ? `完成日期 ${completedRangeLabel}` : null,
							compareSummary ? `对比 ${compareSummary}` : null,
						]
							.filter(Boolean)
							.join(' · ')}
					</span>
				</p>
			) : null}
			{scopeTags.length > 0 ? (
				<div className='statistics-persisted-chart-modal__meta-tags'>
					<span className='statistics-persisted-chart-modal__meta-label'>筛选</span>
					<div className='statistics-persisted-chart-modal__meta-tag-list'>
						{scopeTags.map((tag) => (
							<span key={tag.id} className='order-filter-chip statistics-filter-console__chip'>
								<span className='order-filter-chip__key'>{tag.label}</span>
							</span>
						))}
					</div>
				</div>
			) : null}
			{fixedAtLabel ? (
				<p className='statistics-persisted-chart-modal__meta-footnote'>
					{isFrozenChartSnapshot(snapshot) ? `定格于 ${fixedAtLabel}` : `固定于 ${fixedAtLabel}`}
				</p>
			) : null}
		</div>
	);
};

DashboardChartMeta.propTypes = {
	snapshot: PropTypes.object,
	show: PropTypes.bool,
	isManager: PropTypes.bool,
};

export default memo(DashboardChartMeta);
